import type {
  OrganisationCreditPolicyMode,
  OrganisationTopUpTarget,
} from '@dreamai/types';

export const MODE_LABELS: Record<OrganisationCreditPolicyMode, string> = {
  pooled: 'one shared balance',
  allocated: 'each member gets their own credits',
  allocated_with_overflow: 'own credits first, then the shared balance',
};

export const MODE_DESCRIPTIONS: Record<OrganisationCreditPolicyMode, string> = {
  pooled:
    'Every member uses one shared balance. You can optionally limit how much each member may use in a month.',
  allocated:
    'Each member gets their own credits every month and cannot use more than that.',
  allocated_with_overflow:
    'Each member gets their own credits every month and can use the shared balance once they run out, up to an optional limit.',
};

/**
 * Stable error codes returned by the organisation routes (dream-be
 * lib/organisations/errors.ts). The server message is kept as the fallback.
 */
export type OrganisationErrorCode =
  | 'admin_only'
  | 'organisation_closed'
  | 'billing_not_ready'
  | 'billing_not_configured'
  | 'payment_declined'
  | 'topup_option_unavailable'
  | 'target_not_eligible'
  | 'subscription_exists'
  | 'subscription_required';

const CODED_MESSAGES: Record<OrganisationErrorCode, string> = {
  admin_only: 'Only organisation admins can do that.',
  organisation_closed: 'This organisation has been closed.',
  billing_not_ready:
    'Set up organisation billing (a subscription and a card) before buying credits.',
  billing_not_configured:
    'Organisation billing is not configured on this server yet. Contact Dream support.',
  payment_declined:
    'The organisation’s card was declined. Update the payment method and try again.',
  topup_option_unavailable:
    'That credit pack is no longer available. Reload and pick another.',
  target_not_eligible:
    'Only active members with a secretary seat can receive organisation credits.',
  subscription_exists: 'This organisation already has a subscription.',
  subscription_required: 'Add a subscription to invite members.',
};

export function codedErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as {
    response?: {
      data?: { error?: string; message?: string; code?: string };
    };
  };
  const data = axiosErr?.response?.data;
  const code = data?.code as OrganisationErrorCode | undefined;
  if (code && code in CODED_MESSAGES) return CODED_MESSAGES[code];
  return data?.error || data?.message || fallback;
}

export function formatMinor(amountMinor: number, currency: string): string {
  const amount = amountMinor / 100;
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export function formatCredits(credits: number): string {
  return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(
    Math.round(credits),
  );
}

export function describeTopUpTarget(
  target: OrganisationTopUpTarget,
  names: { groups: Map<string, string>; members: Map<string, string> },
): string {
  switch (target.kind) {
    case 'organisation':
      return 'Shared balance';
    case 'group':
      return `Group: ${names.groups.get(target.groupId) ?? 'deleted group'}`;
    case 'member':
      return names.members.get(target.membershipId) ?? 'Former member';
  }
}
